/**
 * Logger Service sử dụng Seq (Datalust)
 * Tích hợp structured logging cho React application
 */

import * as seq from 'seq-logging';

// Lấy cấu hình từ environment variables
const SEQ_SERVER_URL = process.env.REACT_APP_SEQ_SERVER_URL || 'https://lms-log.exo.io.vn';
const SEQ_API_KEY = process.env.REACT_APP_SEQ_API_KEY || '';
const APP_NAME = process.env.REACT_APP_NAME || 'LMS-App';

// Cấu hình Seq logger
const logger = new seq.Logger({
  serverUrl: SEQ_SERVER_URL,
  apiKey: SEQ_API_KEY,
  onError: (error) => {
    console.error('Seq logging error:', error);
  },
});

// Log levels
export enum LogLevel {
  VERBOSE = 'Verbose',
  DEBUG = 'Debug',
  INFORMATION = 'Information',
  WARNING = 'Warning',
  ERROR = 'Error',
  FATAL = 'Fatal',
}

/**
 * Interface cho structured log properties
 */
export interface LogProperties {
  [key: string]: any;
}

/**
 * Class Logger Service wrapper
 */
class LoggerService {
    /**
   * Log message với level Verbose
   * Note: Không gửi lên server (chỉ console.log local)
   */
  verbose(message: string, properties?: LogProperties) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[VERBOSE]', message, properties);
    }
    // Không emit lên Seq server
  }

  /**
   * Log message với level Debug
   * Note: Không gửi lên server (chỉ console.log local)
   */
  debug(message: string, properties?: LogProperties) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[DEBUG]', message, properties);
    }
    // Không emit lên Seq server
  }

  /**
   * Log message với level Information
   * Note: Không gửi lên server (chỉ console.log local)
   */
  info(message: string, properties?: LogProperties) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[INFO]', message, properties);
    }
    // Không emit lên Seq server
  }

  /**
   * Log message với level Warning
   */
  warn(message: string, properties?: LogProperties) {
    logger.emit({
      timestamp: new Date(),
      level: LogLevel.WARNING,
      messageTemplate: message,
      properties: this.enrichProperties(properties),
    });
  }

  /**
   * Log message với level Error
   */
  error(message: string, error?: Error, properties?: LogProperties) {
    logger.emit({
      timestamp: new Date(),
      level: LogLevel.ERROR,
      messageTemplate: message,
      properties: this.enrichProperties({
        ...properties,
        error: error?.message,
        stack: error?.stack,
      }),
    });
  }

  /**
   * Log message với level Fatal
   */
  fatal(message: string, error?: Error, properties?: LogProperties) {
    logger.emit({
      timestamp: new Date(),
      level: LogLevel.FATAL,
      messageTemplate: message,
      properties: this.enrichProperties({
        ...properties,
        error: error?.message,
        stack: error?.stack,
      }),
    });
  }

    /**
   * Thêm thông tin context tự động vào mọi log
   */
  private enrichProperties(properties?: LogProperties): LogProperties {
    return {
      ...properties,
      application: APP_NAME,
      environment: process.env.NODE_ENV || 'development',
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
  }

    /**
     * Log API request
     * Note: Chỉ log local, không gửi lên server
     */
    logApiRequest(method: string, url: string, data?: any) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${APP_NAME}] API Request`, { method, url, data });
      }
      // Không emit lên Seq server
    }

    /**
     * Log API response
     * Note: Chỉ gửi lên server nếu status >= 400 (error)
     */
    logApiResponse(method: string, url: string, status: number, data?: any) {
      if (status >= 400) {
        // Gửi lên server với level ERROR
        this.error(`[${APP_NAME}] API Response Error`, undefined, {
          method,
          url,
          status,
          data,
          eventType: 'ApiResponse',
        });
      } else {
        // Success response - chỉ log local
        if (process.env.NODE_ENV === 'development') {
          console.log(`[${APP_NAME}] API Response`, { method, url, status });
        }
      }
    }

    /**
   * Log API error
   */
  logApiError(method: string, url: string, error: Error) {
    this.error(`[${APP_NAME}] API Error`, error, {
      method,
      url,
      eventType: 'ApiError',
    });
  }

    /**
     * Log user action
     * Note: Chỉ log local, không gửi lên server
     */
    logUserAction(action: string, details?: LogProperties) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${APP_NAME}] User Action`, { action, ...details });
      }
      // Không emit lên Seq server
    }

    /**
     * Log page view
     * Note: Chỉ log local, không gửi lên server
     */
    logPageView(pageName: string, path: string) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${APP_NAME}] Page View`, { pageName, path });
      }
      // Không emit lên Seq server
    }

    /**
     * Log performance metric
     * Note: Chỉ log local, không gửi lên server
     */
    logPerformance(metric: string, value: number, unit: string = 'ms') {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${APP_NAME}] Performance Metric`, { metric, value, unit });
      }
      // Không emit lên Seq server
    }

    /**
   * Flush logs (đảm bảo tất cả logs được gửi đi)
   */
  async flush(): Promise<void> {
    await logger.flush();
  }

  /**
   * Close logger connection
   */
  close() {
    logger.close();
  }
}

// Export singleton instance
export const loggerService = new LoggerService();

// Export default
export default loggerService;